import { createAction } from "redux-actions";
import { saveAs } from "file-saver";
import XLSX from "xlsx";
import { apiPostFile } from "util/api";
import FormData from "form-data";
import validate from "hakai-upload-validator";
import uploadPropsSelector from "./selectors";
import { deDupArray } from "./components/UploadCommon";
import * as types from "./constants";

const readBinaryFile = file =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = event => resolve(event.target.result);
    // todo: dont use rabs as its deprecated
    reader.readAsBinaryString(file);
  });
export const setSelectedOrganization = createAction(
  types.SET_SELECTED_ORGANIZATION
);

export const setStepIndex = createAction(types.SET_STEP_INDEX);
export const addFiles = createAction(types.UPLOAD_ADD_FILES);
export const removeFile = createAction(types.UPLOAD_REMOVE_FILE);
export const updateFileStatus = createAction(types.UPLOAD_UPDATE_FILE_STATUS);
export const resetFiles = createAction(types.UPLOAD_RESET_FILES);
export const fileUploadSuccess = createAction(types.UPLOAD_FILE_UPLOAD_SUCCESS);
// export const fileUploadError = createAction(types.UPLOAD_FILE_UPLOAD_ERROR);
export const fileUploadError = createAction(
  types.UPLOAD_FILE_UPLOAD_ERROR,
  m => m.error,
  m => ({
    fileIndex: m.fileIndex
  })
);

export const fileSetValidating = createAction(
  types.UPDATE_FILE_IS_SET_VALIDATING
);
export const setIsUploadingFile = createAction(
  types.UPLOAD_SET_FILE_IS_UPLOADING
);
// download a text file log of all the files for debugging
export const downloadLog = () => (dispatch, getState) => {
  const { files } = uploadPropsSelector(getState());

  const dataToSave = files.map(file => ({
    fileName: file.name,
    submission_id: file.submissionID,
    debugLog: file.debugLog
  }));

  const blob = new Blob([JSON.stringify(dataToSave, null, 2)], {
    type: "text/plain;charset=utf-8"
  });
  saveAs(blob, "uploadLog.txt");
};

// save a file to the database, or if isTestInsert is true, do a test insert
export const saveFileToDB = ({ fileIndex, isTestInsert }) => async (
  dispatch,
  getState
) => {
  const { files, email, selectedOrganization } = uploadPropsSelector(
    getState()
  );
  dispatch(setIsUploadingFile({ fileIndex, isUploading: true }));
  const url = "/eims/forms/xlsx/form-data";

  const file = files[fileIndex];
  const fileHandle = file.file;
  const config = {
    headers: {
      email,
      filename: file.name,
      organization: selectedOrganization,
      "commit-transaction": !isTestInsert
    }
  };

  const form = new FormData();
  form.append("file", fileHandle);
  let response;
  try {
    const responseStream = await apiPostFile(url, form, config);
    response = await responseStream.json();
  } catch (error) {
    // note that payload is error and meta is { fileIndex }
    const dist = fileUploadError({
      fileIndex,
      error
    });
    dispatch(dist);
    return;
  }
  // response[0] is the validation log, response[1] is response from API after form is inserted
  // if response is string then its a more serious error, todo should also dispathc new error so modal comes up..
  let errors = [];
  let warnings = [];
  let submissionID;

  if (!Array.isArray(response)) {
    errors.push({ level: "error", message: response.message || response });
  } else {
    const errorsAndWarnings = [];
    response.forEach(res => errorsAndWarnings.push(...res.errors));
    warnings = errorsAndWarnings.filter(
      err => err.level.toUpperCase() === "WARNING"
    );
    errors = errorsAndWarnings.filter(
      err => err.level.toUpperCase() === "ERROR"
    );
    if (response[0]) {
      submissionID = response[0].submission_id;
    }
  }

  dispatch(
    fileUploadSuccess({
      fileIndex,
      // uploadErrors,
      submissionID,
      isTestInsert,
      errors,
      warnings,
      hasValidated: true
    })
  );
};

// save all error-free files to DB (not a test insert)
export const saveAllFilesToDB = () => async (dispatch, getState) => {
  const { files } = uploadPropsSelector(getState());
  let fileIndex = 0;
  // eslint-disable-next-line
  for (const file of files) {
    if (!file.errors.length && !file.hasUploaded) {
      // eslint-disable-next-line
      await dispatch(saveFileToDB({ fileIndex, isTestInsert: false }));
    }
    fileIndex += 1;
  }
};

// Uses the imported hakai-upload-validator to loop through all files and validate.
// If this succeeds without errors, then test insert run next
export const validateFiles = () => async (dispatch, getState) => {
  const {
    files,
    allowedOrganizations,
    selectedOrganization
  } = uploadPropsSelector(getState());
  let fileIndex = 0;
  // eslint-disable-next-line
  for (const file of files) {
    if (!file.hasValidated) {
      dispatch(fileSetValidating({ fileIndex, isValidating: true }));
      // eslint-disable-next-line
      const data = await readBinaryFile(file.file);
      const workbook = XLSX.read(data, { type: "binary" });
      const worksheetsFormatted = Object.keys(workbook.Sheets).map(
        sheetName => ({
          name: sheetName,
          data: XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
            header: 1
          })
        })
      );

      const joiErrors = [];

      const { metadataErrorLog, submissions } = validate(
        worksheetsFormatted,
        "test@test.ca",
        file.name,
        false,
        selectedOrganization,
        allowedOrganizations
      );

      const errors = [];
      const warnings = [];
      const debug = [];
      const info = [];
      const debugLog = [];
      const hasMetaDataErrors = metadataErrorLog && metadataErrorLog.length;
      const unMappedFields = [];

      if (hasMetaDataErrors) {
        errors.push(...metadataErrorLog);
      } else if (submissions) {
        // combining errors for each submission (worksheet)
        // There is a submission object for each sheet, but only showing errors/warnings
        // for each file. so here we are grouping the errors/warnings/debug info/unmapped fields by file
        submissions.forEach(submission => {
          const { debugObj, validationLog } = submission;
          const { mlog, joiLog } = validationLog;

          if (mlog) {
            errors.push(...mlog.filter(err => err.level === "error"));
            warnings.push(...mlog.filter(err => err.level === "warn"));
          }
          if (joiLog) {
            joiErrors.push(...joiLog);
          }
          unMappedFields.push(...debugObj.fieldsNotMapped);

          debugLog.push(debugObj);
        });
      }

      warnings.push(
        ...deDupArray(unMappedFields).map(unmappedField => ({
          level: "warn",
          message: `${unmappedField} not mapped.`
        }))
      );
      const fileHasErrors =
        hasMetaDataErrors || errors.length || joiErrors.length;

      // done after loop so all errors from different sheets can be collected
      // but what if saveFileToDB created an error before updateFileSTatus?
      dispatch(
        updateFileStatus({
          fileIndex,
          errors,
          warnings,
          joiErrors,
          debugLog,
          metadataErrorLog,
          debug,
          info,
          // if there are errors thats it, if not it proceeds to next step
          hasValidated: fileHasErrors,
          isValidating: !fileHasErrors
        })
      );

      if (!fileHasErrors) {
        // eslint-disable-next-line
        await dispatch(saveFileToDB({ fileIndex, isTestInsert: true }));
      }
    } // end file loop
    fileIndex += 1;
  }
};
